import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputOtp } from 'primereact/inputotp'
import { toast } from 'react-toastify'
import { useDebouncedCallback } from 'use-debounce'
import { INDIAN_MOBILE_NO_PATTERN } from '../../../../utils/pattern'
import { AxiosResponse } from 'axios'
import { APIResponseEntity } from '../../../../interface/api-responce'
import { APIToSendWhatsappOTPForTheTrustSeal, APIToVerifyTrustSealWhatsappOTP } from '../../../../service/api-service'
import { toastTrustSealOptObj } from '../../utils/constant'
import { Loader2 } from 'lucide-react'
import { IsStringNullEmptyOrUndefined } from '../../../../utils/null-check'

interface WhatsAppOtpDialogProps {
    visible: boolean
    onHide: () => void
    trustSealId: string
    onVerified: () => void
}

interface DialogFeedback {
    type: 'success'
    message: string
}

const WHATSAPP_NUMBER_LENGTH = 10
const OTP_LENGTH = 4

const WhatsAppOtpDialog: React.FC<WhatsAppOtpDialogProps> = ({ visible, onHide, trustSealId, onVerified }) => {
    const [mobileNumber, setMobileNumber] = useState<string>('')
    const [otpValue, setOtpValue] = useState<string>('')
    const [otpSent, setOtpSent] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [validationError, setValidationError] = useState<string>('')
    const [hasTouchedMobile, setHasTouchedMobile] = useState<boolean>(false)
    const [currentTrustSealId, setCurrentTrustSealId] = useState(trustSealId)
    const [feedback, setFeedback] = useState<DialogFeedback | null>(null)
    const [lastAttemptedOtp, setLastAttemptedOtp] = useState<string>('')

    useEffect(() => {
        setCurrentTrustSealId(trustSealId)
    }, [trustSealId])

    useEffect(() => {
        if (!visible) {
            setMobileNumber('')
            setOtpValue('')
            setOtpSent(false)
            setIsLoading(false)
            setValidationError('')
            setHasTouchedMobile(false)
            setFeedback(null)
            setLastAttemptedOtp('')
        }
    }, [visible])

    const sanitizedMobileNumber = mobileNumber.replace(/\D/g, '').slice(0, WHATSAPP_NUMBER_LENGTH)
    const isWhatsAppNumberValid = INDIAN_MOBILE_NO_PATTERN.test(sanitizedMobileNumber)
    const isWhatsAppNumberInvalid = (!!validationError || hasTouchedMobile) && !isWhatsAppNumberValid

    const handleSendOtp = async (): Promise<void> => {
        if (!isWhatsAppNumberValid) {
            setHasTouchedMobile(true)
            setValidationError('Enter a valid 10-digit WhatsApp number')
            return
        }

        setValidationError('')
        setFeedback(null)
        setIsLoading(true)
        try {
            const resp: AxiosResponse<APIResponseEntity> =
                await APIToSendWhatsappOTPForTheTrustSeal({
                    trustSealUserId: currentTrustSealId,
                    mobileNo: sanitizedMobileNumber
                })

            if (!resp || resp.status !== 200 || !resp.data) {
                return
            }

            const apiResp: APIResponseEntity = resp.data

            if (
                apiResp.status &&
                apiResp.statusCode === 200 &&
                apiResp.data
            ) {
                if (apiResp.data.trustSealUserId && !IsStringNullEmptyOrUndefined(apiResp.data.trustSealUserId)) {
                    setCurrentTrustSealId(apiResp.data.trustSealUserId)
                }
                setOtpSent(true)
            } else {
                toast.error(apiResp.message || apiResp.errorMsg, {
                    ...toastTrustSealOptObj,
                })
            }
        } catch (error) {
            console.log(error, 'error')
        } finally {
            setIsLoading(false)
        }
    }

    const handleVerifyOtp = useCallback(async (): Promise<void> => {
        const trimmedOtp = otpValue.trim()

        if (trimmedOtp.length !== OTP_LENGTH) {
            return
        }

        // Prevent re-verifying the same OTP
        if (trimmedOtp === lastAttemptedOtp) {
            return
        }

        setLastAttemptedOtp(trimmedOtp)
        setIsLoading(true)
        setFeedback(null)
        try {
            const resp: AxiosResponse<APIResponseEntity> =
                await APIToVerifyTrustSealWhatsappOTP({
                    trustSealUserId: currentTrustSealId,
                    OTP: trimmedOtp
                })

            if (!resp || resp.status !== 200 || !resp.data) {
                toast.error('Unable to verify OTP at the moment', {
                    ...toastTrustSealOptObj,
                })
                return
            }

            const apiResp: APIResponseEntity = resp.data

            if (
                apiResp.status &&
                apiResp.statusCode === 200 &&
                apiResp.data
            ) {
                setFeedback({
                    type: 'success',
                    message: apiResp.message || 'OTP verified successfully'
                })
                setOtpValue('')
                setLastAttemptedOtp('');
                onVerified();
            } else {
                toast.error(apiResp.message || apiResp.errorMsg || 'Unable to verify OTP', {
                    ...toastTrustSealOptObj,
                })
            }
        } catch (error) {
            toast.error('Unable to verify OTP at the moment', {
                ...toastTrustSealOptObj,
            })
        } finally {
            setIsLoading(false)
        }
    }, [otpValue, currentTrustSealId, lastAttemptedOtp])

    const debouncedVerifyOtp = useDebouncedCallback(() => {
        void handleVerifyOtp()
    }, 400)

    useEffect(() => {
        if (!otpSent || isLoading || otpValue.trim().length !== OTP_LENGTH) {
            return
        }

        debouncedVerifyOtp()

        return () => {
            debouncedVerifyOtp.cancel()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpSent, otpValue, isLoading])

    // Reset lastAttemptedOtp when user manually changes the OTP
    useEffect(() => {
        if (otpValue.trim() !== lastAttemptedOtp && lastAttemptedOtp.length > 0) {
            setLastAttemptedOtp('')
        }
    }, [otpValue, lastAttemptedOtp])

    const handleClose = (): void => {
        if (isLoading) {
            return
        }

        if (feedback) {
            onVerified();
        } else {
            onHide()
        }

    }

    return (
        <Dialog
            header="Verify via WhatsApp"
            visible={visible}
            draggable={false}
            resizable={false}
            modal
            closable={true}
            onHide={handleClose}
            className="trustseal-otp-dialog p-fluid"
            breakpoints={{ '960px': '76vw', '640px': '92vw' }}
            style={{ width: '32rem', maxWidth: '95vw' }}
            contentStyle={{ padding: '1.25rem 1.5rem' }}
        >
            <div className="trustseal-otp-body">
                <div>
                    <p className={`trustseal-otp-heading ${feedback ? 'hidden' : ''}`}>Secure your quotation with WhatsApp OTP</p>
                    <p className={`trustseal-otp-description ${feedback ? 'hidden' : ''}`}>
                        Enter your mobile number to receive a verification code on WhatsApp. This keeps your quotation linked to your phone and enables quick PDF delivery.
                    </p>
                </div>

                {!otpSent && !feedback && (
                    <div className="trustseal-otp-field">
                        <label htmlFor="whatsapp-number">WhatsApp mobile number</label>
                        <InputText
                            id="whatsapp-number"
                            onChange={(e) => {
                                setMobileNumber(e.target.value)
                                if (validationError) {
                                    setValidationError('')
                                }
                            }}
                            onBlur={() => {
                                setHasTouchedMobile(true)
                                if (mobileNumber && !isWhatsAppNumberValid) {
                                    setValidationError('Enter a valid 10-digit WhatsApp number')
                                } else {
                                    setValidationError('')
                                }
                            }}
                            placeholder="Enter 10 digit number"
                            maxLength={13}
                            className={`inputtext-whatsapp-number ${isWhatsAppNumberInvalid ? 'p-invalid' : ''}`}
                            style={isWhatsAppNumberInvalid ? { borderColor: 'red' } : undefined}
                        />
                        {isWhatsAppNumberInvalid && (
                            <small className="text-danger font-weight-500 d-block mt-1">
                                Enter a valid 10-digit WhatsApp number
                            </small>
                        )}
                    </div>
                )}

                {!otpSent && !feedback && (
                    <div className="trustseal-otp-field">
                        <Button
                            type="button"
                            icon="pi pi-send"
                            iconPos="left"
                            onClick={handleSendOtp}
                            disabled={isLoading}
                            className="p-button-rounded p-button-sm p-button-success"
                            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                        >
                            {isLoading ? <Loader2 className='spin' size={16} /> : 'Send OTP'}
                        </Button>
                    </div>
                )}

                {otpSent && !feedback && (
                    <div className="trustseal-otp-verify-section">
                        <div className="trustseal-otp-verify-input">
                            <InputOtp
                                id="whatsapp-otp"
                                value={otpValue}
                                onChange={(e) => setOtpValue(e.value ? String(e.value) : '')}
                                integerOnly
                                length={OTP_LENGTH}
                                disabled={isLoading}
                                pt={{ input: { style: { width: '3rem', height: '3rem', borderRadius: '8px' } } }}
                            />
                        </div>
                        {isLoading && (
                            <div className="trustseal-otp-loading">
                                <Loader2 size={20} className="spin" />
                                <span>Verifying OTP...</span>
                            </div>
                        )}
                        <div className="trustseal-otp-actions">
                            <Button
                                type="button"
                                onClick={handleClose}
                                disabled={isLoading}
                                text
                                style={{ alignSelf: 'center', display: 'flex', justifyContent: 'center' }}
                                className="p-button-rounded"
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}

                {feedback && (
                    <div className="trustseal-otp-feedback">
                        <div className="trustseal-otp-feedback-content">
                            <p className="trustseal-otp-feedback-message">{feedback.message}</p>
                        </div>
                        <Button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                            className="p-button-rounded p-button-success"
                            style={{ alignSelf: 'center', minWidth: '200px', display: 'flex', justifyContent: 'center' }}
                        >
                            Close
                        </Button>
                    </div>
                )}
            </div>
        </Dialog>
    )
}

export default WhatsAppOtpDialog
